import {
  type FilterInterfaceFilter,
  type FormattingFilter,
  type GroupingRule,
  ViewIds,
} from '@ardoq/api-types';
import type { APIOrganizationUser, ArdoqId } from '@ardoq/api-types';
import type {
  ContextShape,
  LoadedGraphWithViewpointMode,
  LoadedScenarioData,
  ShowRelatedComponentsShape,
} from '@ardoq/data-model';
import type { I18nLocale } from '@ardoq/i18n';
import {
  getIsShowReferenceTypeApplied,
  type ScopeDataParams,
} from '@ardoq/graph';
import { filterOperations, usersOperations } from '@ardoq/core';
import {
  type ActiveConditionalFormattingForLegend,
  buildMinimalScopeDataForFilters,
  getActiveConditionalFormattingForLegendPure,
} from '@ardoq/view-legend';
import { getViewpointRelationshipsDiagramFlatEmptyState } from 'tabview/relationshipDiagrams/utils';
import { getDefaultRelationshipDiagramsViewInterface } from 'tabview/relationshipDiagrams/viewModel$/getDefaultRelationshipDiagramsViewInterface';
import { getRawGraphWithScenarioData } from 'tabview/relationshipsDiagramsViewModel/getRawGraphWithScenarioData';
import { getGraphData } from 'tabview/relationshipsDiagramsViewModel/getGraphData';
import {
  getPureGraphItems,
  getViewpointModeLabelsData,
} from 'tabview/relationshipDiagrams/viewModel$/utils';
import { toViewModel } from 'tabview/relationshipDiagrams/viewModel$/viewModelFromGraph';
import type { BlocksViewSettings, BlocksViewSourceDataShape } from '../types';
import { blocksViewRepresentationData } from '../blocksViewRepresentationData';

const VIEW_ID = ViewIds.BLOCKS;

const EMPTY_FORMATTING_FOR_LEGEND = {
  components: [],
  references: [],
  tags: [],
};

export const getEmptyBlocksViewSourceData = (
  viewInstanceId: string
): BlocksViewSourceDataShape => ({
  ...getViewpointRelationshipsDiagramFlatEmptyState(VIEW_ID),
  viewInstanceId,
  viewId: VIEW_ID,
  activeConditionalFormattingForLegend: EMPTY_FORMATTING_FOR_LEGEND,
  formattingFilters: [],
});

export type BuildBlocksViewSourceDataArgs = {
  context: ContextShape;
  loadedGraph: LoadedGraphWithViewpointMode;
  loadedScenarioData: LoadedScenarioData;
  scenarioRelatedComponents: ShowRelatedComponentsShape;
  showScopeRelated: boolean;
  /** Duration-tracking session the caller started, or null to not track. */
  performanceTrackingId: string | null;
  i18nLocale: I18nLocale;
  orgUsersById: Record<ArdoqId, APIOrganizationUser>;
  viewSettings: BlocksViewSettings;
  users: APIOrganizationUser[];
  formattingAndLabelRules: (FilterInterfaceFilter | FormattingFilter)[];
  groupingRules: GroupingRule[];
};

/**
 * Sets the right args for buildViewModel and creates the source data shape
 */
export const buildBlocksViewSourceData = (
  previousState: BlocksViewSourceDataShape,
  {
    context,
    loadedGraph,
    loadedScenarioData,
    scenarioRelatedComponents,
    showScopeRelated,
    performanceTrackingId,
    viewSettings,
    users,
    i18nLocale,
    orgUsersById,
    groupingRules,
    formattingAndLabelRules: formattingRulesInTheAppState,
  }: BuildBlocksViewSourceDataArgs
): BlocksViewSourceDataShape => {
  const graphModel = loadedGraph.scopeData.graphData;

  const {
    viewInstanceId,
    scopeDiffState: { scopeDiff },
  } = previousState;

  const scopeDataParams: ScopeDataParams = {
    enhancedScopeData: loadedGraph.scopeData,
    formattingFilters: formattingRulesInTheAppState,
    groupingRules,
    i18nLocale,
    formatUserFieldFn: val =>
      usersOperations.formatUserField(orgUsersById, val),
  };

  const viewInterface =
    getDefaultRelationshipDiagramsViewInterface(scopeDataParams);

  // collapsed group ids in BlocksView are stored in the layout data
  const collapsedGroupIds = new Set<string>();

  const rawGraph = getRawGraphWithScenarioData({
    viewInterface,
    loadedScenarioData,
    scenarioRelatedComponents,
    showScopeRelated,
    graphModel,
    viewId: VIEW_ID,
    loadedGraph,
    isInScopeDiffMode: Boolean(scopeDiff),
    collapsedGroupIds,
    shouldCollapseGraph: false,
    userDefinedGroupingRules: groupingRules,
    i18nLocale,
  });

  const {
    componentMap,
    groupMap,
    referenceList,
    componentIdsOfCollapsedGroups,
    errors,
    hasClones,
  } = rawGraph;

  const getRepresentationData = (componentId: string) =>
    blocksViewRepresentationData(
      viewInterface,
      componentId,
      viewSettings.componentStyle
    );

  const graphData = getGraphData({
    ...rawGraph,
    viewInterface,
    getRepresentationData,
  });

  // labels data is indexed by graph item id
  const { nodeLabelsData, edgeLabelsData } = getViewpointModeLabelsData({
    viewInterface,
    componentMap,
    groupMap,
    rawReferences: referenceList,
    isShowReferenceTypeApplied: getIsShowReferenceTypeApplied(
      viewInterface.getAllReferenceLabelFilters()
    ),
    i18nLocale,
  });

  const { graphNodes, groupNodes, edges } = getPureGraphItems({
    i18nLocale,
    viewInterface,
    groupMap,
    componentMap,
    rawReferences: referenceList,
    componentIdsOfCollapsedGroups,
    rawCollapsedGroupIds: collapsedGroupIds,
    scopeDiff,
    contextComponentId: context.componentId,
    nodeLabelsData,
    edgeLabelsData,
    ...graphData,
  });

  const formattingFilters = formattingRulesInTheAppState.filter(
    filterOperations.isStrictlyFormattingFilter
  );

  const scopeData = buildMinimalScopeDataForFilters(
    viewInterface,
    formattingFilters,
    users
  );

  const activeConditionalFormattingForLegend: ActiveConditionalFormattingForLegend =
    getActiveConditionalFormattingForLegendPure(
      scopeData,
      formattingFilters,
      i18nLocale,
      true
    );

  return {
    ...previousState,
    viewInterface,
    componentIdsOfCollapsedGroups,
    expandedStateChangingGroupId: null,
    errors,
    hasClones,
    ...graphData,
    nodes: toViewModel([...graphNodes.values()]),
    edges: toViewModel(edges),
    groups: toViewModel([...groupNodes.values()]),
    viewSettings,
    context,
    graphModel,
    loadedScenarioData,
    scenarioRelatedComponents,
    showScopeRelated,
    rawGraph,
    getRepresentationData,
    performanceTrackingId,
    viewInstanceId,
    loadedGraph,
    viewId: VIEW_ID,
    activeConditionalFormattingForLegend,
    formattingFilters,
  };
};
